import { CalendarDay } from "../calendar/utils";
import { getPsalmRefFromText } from "../chants/utils";
import { LoadableDataDependency } from "../utils/RemoteDataProvider";
import { LessonData, Settings } from "../utils/settings";
import { getComplineCanticleAntiphon } from "./compline-canticle-antiphon";
import { getComplinePsalmAntiphon } from "./compline-psalm-antiphon";
import { getComplineResponsory } from "./compline-responsory";
import { getLesserLitany } from "./lesser-litany";
import { getLetUsBlessTheLord } from "./let-us-bless-the-lord";
import { getLordsPrayer } from "./lords-prayer";
import { getOpeningResponses } from "./opening-responses";
import { getProperCollect } from "./proper-collect";
import { getSalutation } from "./salutation";
import { getTurnUsThen } from "./turn-us-then";
import { LiturgyItem } from "./types";
export const getComplineLiturgy = (
    day: CalendarDay,
    settings: Settings,
    loadablePsalms: LoadableDataDependency<string[]>,
    loadableLessons: LoadableDataDependency<{
        firstLesson: LessonData;
        secondLesson: LessonData;
    }>
) => {
    const items: LiturgyItem[] = [];

    const psalmRefs =
        settings.psalmsAtCompline === "compline-short"
            ? [["4", "134"], ["31:1-16"], ["91"]][day.dayOfTheYear % 3]
            : settings.psalmsAtCompline === "compline"
            ? ["4", "31:1-16", "91", "134"]
            : loadablePsalms.state === "loaded"
            ? loadablePsalms.value
            : [];

    items.push({ type: "heading", day, hour: "compline" });

    items.push({
        type: "rubric",
        content: `All standing, the Officiant shall begin with this prayer,
            **Short_Lesson**, and versicle, the People responding.`,
    });

    items.push({
        type: "text",
        content: `The Lord almighty grant us a quiet night and a perfect end.\\n**Amen.**\\n\\nBrethren, be sober, be vigilant; because your adversary the devil, as a roaring lion, walketh about, seeking whom he may devour: whom resist, steadfast in the faith.\\n\\nBut thou, O Lord, have mercy upon us.\\n**Thanks be to God.**\\n\\nOur help is in the name of the Lord;\\n**Who hath made heaven and earth.**`,
    });

    items.push({ type: "spacer" });

    items.push({
        type: "rubric",
        content: `All shall kneel and say the **Confession.**`,
    });

    items.push({
        type: "text",
        content: `**Almighty God and Father, we confess to thee, to one another, and
            to the whole host of heaven, that we have sinned against thee,
            through our own fault, in thought, and word, and deed, and in
            those things which we have left undone. For the sake of thy Son
            our Lord Jesus Christ, have mercy upon us, forgive us our sins,
            and by the power of the Holy Ghost raise us up to serve thee in
            newness of life, to the glory of thy holy Name. Amen.**`,
    });

    items.push({ type: "spacer" });

    items.push({
        type: "rubric",
        content: `The Officiant and People shall sing this **Versicle_and_Response.**`,
    });

    items.push(...getTurnUsThen());

    items.push({ type: "spacer" });

    items.push({
        type: "rubric",
        content: `All standing, the Officiant and People shall sing these **Preces_and_Responses.**`,
    });

    items.push(...getOpeningResponses(true));

    items.push({ type: "spacer" });

    if (settings.hymnPosition === "before psalms") {
        items.push({
            type: "rubric",
            content: `The **Office Hymn** may be sung.`,
            settingsPanelId: "office hymn",
        });
        items.push({ type: "office hymn", day, hour: "compline" });
        items.push({ type: "spacer" });
    }

    items.push({
        type: "rubric",
        content: `All sitting, the **${
            psalmRefs.length === 1 ? "Psalm" : "Psalms"
        }** shall be sung.`,
        settingsPanelId: "compline psalms",
    });

    if (settings.psalmsAtCompline !== "same as evensong") {
        items.push(...getComplinePsalmAntiphon());
    }

    if (psalmRefs.length > 0) {
        psalmRefs.forEach((psalmRef) => {
            items.push({
                type: "psalm",
                psalmRef: getPsalmRefFromText(psalmRef),
            });
        });
    } else {
        items.push({ type: "loading" });
    }

    if (settings.psalmsAtCompline !== "same as evensong") {
        items.push(...getComplinePsalmAntiphon());
    }

    items.push({ type: "spacer" });

    items.push({
        type: "rubric",
        content: `The **Lesson** shall_be_read.`,
        settingsPanelId: "compline lesson",
    });

    if (settings.secondReadingToCompline === "yes") {
        if (
            loadableLessons.state === "loaded" &&
            Array.isArray(loadableLessons.value.secondLesson)
        ) {
            loadableLessons.value.secondLesson.forEach(
                (lessonPart: { title: string; content: any }, index) => {
                    items.push({
                        type: "scripture",
                        title: lessonPart.title,
                        content: lessonPart.content,
                        hasBeginneth: index === 0,
                    });
                }
            );

            items.push({
                type: "text",
                content: `Here endeth the lesson.`,
            });
        } else {
            items.push({ type: "loading" });
        }
    } else {
        items.push({
            type: "text",
            content: `Thou, O Lord, art in the midst of us, and we are called by
                thy name. Leave us not, O Lord our God.\\n\\n**Thanks be to God.**`,
        });
    }

    items.push({ type: "spacer" });

    items.push({
        type: "rubric",
        content: `The Officiant and People shall sing this **Responsory.**`,
    });

    items.push(...getComplineResponsory());

    items.push({ type: "spacer" });

    if (settings.hymnPosition === "before canticle") {
        items.push({
            type: "rubric",
            content: `The **Office Hymn** may be sung.`,
            settingsPanelId: "office hymn",
        });
        items.push({ type: "office hymn", day, hour: "compline" });
        items.push({ type: "spacer" });
    }

    items.push({
        type: "rubric",
        content: `All standing, the **Gospel_Canticle** shall be sung.`,
        settingsPanelId: "gospel canticles",
    });

    items.push(...getComplineCanticleAntiphon());

    items.push({ type: "canticle", id: "nunc dimittis", tone: "3a.4", day });

    items.push(...getComplineCanticleAntiphon());

    items.push({ type: "spacer" });

    items.push({
        type: "rubric",
        content: `The Officiant shall sing the **Salutation**, the_People_responding.`,
        settingsPanelId: "salutation",
    });

    items.push(...getSalutation(settings));

    items.push({
        type: "rubric",
        content: `All kneeling, the **Prayers** shall be sung.`,
    });

    items.push(...getLesserLitany());

    items.push(...getLordsPrayer(false, settings));

    items.push({
        type: "rubric",
        content: `One or more of the following **Collects** shall be said or sung.`,
    });

    items.push({
        type: "text",
        content: `Visit, we beseech thee, O Lord, this place, and drive far from
            it all snares of the enemy; Let thy holy Angels dwell herein to
            preserve us in peace, and may thy blessing be upon us evermore;
            through Jesus Christ our Lord.\n**Amen.**`,
    });

    items.push(...getProperCollect(day, "compline", true, settings));

    if (settings.hymnPosition === "anthem") {
        items.push({
            type: "rubric",
            content: `The **Office Hymn** may be sung.`,
            settingsPanelId: "office hymn",
        });
        items.push({ type: "office hymn", day, hour: "compline" });
    }

    items.push({ type: "spacer" });

    items.push({
        type: "rubric",
        content: `All standing, the Officiant shall sing, the_People_responding,`,
    });

    items.push(...getLetUsBlessTheLord(day, "compline"));

    items.push({ type: "spacer" });

    items.push({
        type: "text",
        content: `The Almighty and Merciful Lord, Father, Son and Holy Ghost,
            bless us and keep us, this night and evermore. **Amen.**`,
    });

    items.push({ type: "spacer", big: true });

    items.push({ type: "footer", day, hour: "compline" });

    return items;
};
